import { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import useAuth from '../hooks/useAuth';
import { useTheme } from '../context/ThemeContext';

const UserProfile = () => {
    const { user, login, updateUser } = useAuth(); // login used to update context if needed
    const { theme, setTheme } = useTheme();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    // Password state
    const [oldPassword, setOldPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [message, setMessage] = useState('');
    const [msgType, setMsgType] = useState('success'); // 'success' or 'error'

    useEffect(() => {
        if (user) {
            setName(user.name || '');
            setEmail(user.email || '');
        }
    }, [user]);

    const showMessage = (msg, type = 'success') => {
        setMessage(msg);
        setMsgType(type);
        setTimeout(() => setMessage(''), 5000);
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        try {
            const updatedUser = {
                name,
                email,
            };

            const res = await apiClient.put('/users/me', updatedUser);

            // Update immediately the local user context
            updateUser(res.data);

            showMessage('Profil mis à jour avec succès', 'success');
        } catch (error) {
            showMessage(error.response?.data?.message || 'Erreur lors de la mise à jour du profil', 'error');
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            showMessage('Les mots de passe ne correspondent pas', 'error');
            return;
        }

        if (!oldPassword) {
            showMessage("Veuillez entrer votre ancien mot de passe", 'error');
            return;
        }

        try {
            await apiClient.put('/users/me/password', {
                oldPassword: oldPassword,
                newPassword: password
            });

            showMessage('Mot de passe mis à jour avec succès', 'success');
            setOldPassword('');
            setPassword('');
            setConfirmPassword('');
        } catch (error) {
            showMessage(error.response?.data?.message || 'Erreur lors de la mise à jour du mot de passe', 'error');
        }
    };

    return (
        <div className="profile-container page-transition">
            <h1 className="profile-title">Profil Utilisateur</h1>

            {message && (
                <div className={`popup-notification ${msgType === 'success' ? 'success' : 'error'}`}>
                    <span>{msgType === 'success' ? '✅' : '⚠️'}</span>
                    {message}
                </div>
            )}

            <div className="card" style={{ marginBottom: '30px', flexDirection: 'column' }}>
                <div style={{ marginBottom: '20px' }}>
                    <h3>Apparence</h3>
                    <p className="card-subtitle">Personnalisez votre expérience</p>
                </div>

                <div className="theme-selector-grid">
                    {[
                        { id: 'default', label: 'Défaut', color: '#1B7A6E', bg: '#FAF9F7' },
                        { id: 'girly', label: 'Girly', color: '#E84393', bg: '#FFF5F7' },
                        { id: 'neutral', label: 'Neutre', color: '#2D3436', bg: '#F5F6FA' }
                    ].map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTheme(t.id)}
                            className={`theme-btn-card ${theme === t.id ? 'active' : ''}`}
                            style={{
                                border: theme === t.id ? `3px solid ${t.color}` : '2px solid transparent',
                                background: t.bg,
                            }}
                        >
                            <div className="theme-color-preview" style={{ background: t.color }}></div>
                            <span className="theme-label" style={{ color: t.color }}>{t.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Informations Personnelles Section */}
            <div className="card" style={{ flexDirection: 'column', marginBottom: '30px' }}>
                <h3>Informations Personnelles</h3>
                <p className="card-subtitle">Mettez à jour vos coordonnées</p>

                <form onSubmit={handleUpdateProfile} className="profile-form">
                    <div className="profile-form-grid">
                        <div className="input-group">
                            <label className="input-label">Nom complet</label>
                            <input
                                className="input-field"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div className="input-group">
                            <label className="input-label">Email</label>
                            <input
                                className="input-field"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn btn-primary" style={{ padding: '12px 40px' }}>
                            Enregistrer les informations
                        </button>
                    </div>
                </form>
            </div>

            {/* Sécurité Section */}
            <div className="card" style={{ flexDirection: 'column' }}>
                <h3>Sécurité</h3>
                <p className="card-subtitle">Changez votre mot de passe</p>

                <form onSubmit={handleUpdatePassword} className="profile-form">
                    <div className="input-group" style={{ marginBottom: '15px' }}>
                        <label className="input-label">Ancien mot de passe</label>
                        <input
                            className="input-field"
                            type="password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            placeholder="••••••••"
                            autoComplete="current-password"
                        />
                    </div>
                    <div className="profile-form-grid">
                        <div className="input-group">
                            <label className="input-label">Nouveau mot de passe</label>
                            <input
                                className="input-field"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="input-group">
                            <label className="input-label">Confirmer le mot de passe</label>
                            <input
                                className="input-field"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{ padding: '12px 40px', backgroundColor: '#e17055', borderColor: '#e17055' }}
                        >
                            Modifier le mot de passe
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserProfile;
